import {
  AllWorkspacesDocument,
  RecentWorkspacesDocument,
  UpdateWorkspaceInput,
  Workspace,
  useUpdateWorkspaceMutation,
} from "@gql/sites-api/graphql";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";

import { ApolloError } from "@apollo/client";
import { Icon } from "react-migration/components/Icon";
import { WORKSPACE_TITLE_ALREADY_EXISTS_CODE } from "../WorkspaceModal/constants";
import { WorkspaceModal } from "../WorkspaceModal/WorkspaceModal";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { useCallback } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type EditWorkspaceModalProps = {
  workspace: Pick<Workspace, "id" | "title" | "developmentType" | "goal" | "image" | "imageUrl">;
  onClose: () => void;
};

export const EditWorkspaceModal = ({ workspace, onClose }: EditWorkspaceModalProps) => {
  const { t } = useTranslation();

  const [updateWorkspace, { loading, error, reset }] = useUpdateWorkspaceMutation({
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    refetchQueries: [AllWorkspacesDocument, RecentWorkspacesDocument],
  });

  const handleUpdateWorkspace = useCallback(
    async (input: Omit<UpdateWorkspaceInput, "id">) => {
      try {
        const { errors } = await updateWorkspace({
          variables: {
            input: {
              id: workspace.id,
              ...input,
            },
          },
        });

        if (!errors?.length) {
          logEventV2({ name: "Workspace edited", properties: { workspaceId: workspace.id } });
          onClose();
        }
      } catch (error) {
        console.error("Failed to update workspace:", error);
      }
    },
    [updateWorkspace, onClose, workspace.id]
  );

  const errorMessage = getTitleErrorMessage(error);
  const hasGenericError = !!error && !errorMessage;

  return (
    <WorkspaceModal
      title={<EditWorkspaceModalTitle />}
      workspace={{
        name: workspace.title,
        developmentType: workspace.developmentType[0],
        goal: workspace.goal,
        image: workspace.image,
        imageUrl: workspace.imageUrl,
      }}
      actionLabel={t("workspaces.modal.update_workspace")}
      handleAction={handleUpdateWorkspace}
      onClose={onClose}
      loading={loading}
      titleError={errorMessage}
      genericError={hasGenericError}
      onErrorDismiss={reset}
    />
  );
};

const EditWorkspaceModalTitle = () => {
  const { t } = useTranslation();
  return (
    <span className="flex items-center text-lg font-semibold gap-x-2">
      <Icon icon="icon-lt-edit-line" size="lg" />
      <span>{t("workspaces.modal.edit_workspace")}</span>
    </span>
  );
};

function getTitleErrorMessage(error?: ApolloError) {
  const titleError = error?.graphQLErrors?.find(
    (error) => error.extensions?.code === WORKSPACE_TITLE_ALREADY_EXISTS_CODE
  );
  return titleError?.message;
}
